import React from 'react';
/**
 * `SummaryPersonalDetails` renders a read-only summary of a customer's personal
 * details and address.
 *
 * Notes:
 * - Date of birth is hidden when `isConfirmationScreen` is true.
 * - Country display name is derived from `addressDetails.country`.
 * - Title is translated based on `countryCode` for non-GB journeys.
 */

import { PersonalDetailType } from '../CommonTypes';
import { SummaryItem } from './Summary';
import { useTranslation } from 'react-i18next';

export type SummaryPersonalDetailsProps = {
  personalDetail: PersonalDetailType;
  isConfirmationScreen: boolean;
  culture: string;
  countryCode: string;
  phoneNumberLabel?: string;
};

const SummaryPersonalDetails = ({
  personalDetail,
  isConfirmationScreen,
  culture,
  countryCode,
  phoneNumberLabel
}: SummaryPersonalDetailsProps): JSX.Element => {
  const { t } = useTranslation();

  const dateofBirth =
    personalDetail.dateOfBirth !== undefined
      ? personalDetail.dateOfBirth?.toLocaleDateString(culture, {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit'
        })
      : '';

  const titleLabel = t('summary.title-label');
  const firstNameLabel = t('summary.first-name-label');
  const surnameLabel = t('summary.surname-label');
  const dateOfBirthLabel = t('summary.date-of-birth-label');
  const emailAddressLabel = t('summary.email-address-label');
  const phoneNumberText = phoneNumberLabel || t('summary.phone-number-label');
  const addressLine1 = t('summary.address-line1');
  const addressLine2 = t('summary.address-line2');
  const cityLabel = t('summary.city-label');
  const postCodeLabel = t('summary.post-code-label');
  const countryLabel = t('summary.country-label');
  const countyLabel = t('summary.county-label');

  const getCountryName = () => {
    switch (personalDetail.addressDetails.country) {
      case 'GBR':
        return t('countries.united-kingdom');
      case 'DEU':
        return t('countries.germany');
      case 'AUT':
        return t('countries.austria');
      default:
        return t('countries.united-kingdom');
    }
  };

  const getTranslatedTitle = () => {
    if (countryCode === 'gb') {
      return personalDetail.title;
    } else if (countryCode === 'es') {
      switch (personalDetail.title) {
        case 'Mr':
          return 'Sr';
        case 'Ms':
          return 'Sra';
        default:
          personalDetail.title;
      }
    } else {
      switch (personalDetail.title) {
        case 'Mr':
          return 'Herr';
        case 'Ms':
          return 'Frau';
        case 'Dr':
          return 'Dr.';
        case 'Sheikh':
          return 'Scheich';
        case 'Prof':
          return 'Prof.';
        default:
          personalDetail.title;
      }
    }
  };

  return (
    <div className='flex-column'>
      <SummaryItem label={titleLabel} value={getTranslatedTitle() ?? ''} />
      <SummaryItem label={firstNameLabel} value={personalDetail.firstName} />
      <SummaryItem label={surnameLabel} value={personalDetail.surname} />
      {!isConfirmationScreen && (
        <SummaryItem label={dateOfBirthLabel} value={dateofBirth} />
      )}
      <SummaryItem label={emailAddressLabel} value={personalDetail.email} />
      <SummaryItem label={phoneNumberText} value={personalDetail.phone} />
      <SummaryItem
        label={addressLine1}
        value={personalDetail.addressDetails.addressLine1 || ''}
      />
      <SummaryItem
        label={addressLine2}
        value={personalDetail.addressDetails.addressLine2 || ''}
      />
      <SummaryItem
        label={cityLabel}
        value={personalDetail.addressDetails.townOrCity || ''}
      />
      <SummaryItem
        label={countyLabel}
        value={personalDetail.addressDetails.region || ''}
      />
      <SummaryItem label={countryLabel} value={getCountryName()} />
      <SummaryItem
        label={postCodeLabel}
        value={personalDetail.addressDetails.postCode || ''}
      />
    </div>
  );
};

export default SummaryPersonalDetails;
